import { useEffect, useState, useCallback } from "react";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import NotificationsPanel from "../components/NotificationsPanel";
import GatewaySettings from "../components/GatewaySettings";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Copy, Plus, Send, Webhook, Trash2, Facebook, Chrome } from "lucide-react";
import { toast } from "sonner";

const SYSTEM_FIELDS = ["name", "email", "phone", "city", "state", "country", "pin_code", "course", "specialization", "campus", "instance"];
const FIELD_TYPES = ["text", "select", "number", "date", "multi_select", "document_upload"];

const CHANNELS = {
  webhook: { label: "Generic Webhook", icon: Webhook, tint: "bg-indigo-50 text-indigo-600" },
  meta_ads: { label: "Meta Lead Ads", icon: Facebook, tint: "bg-blue-50 text-blue-600" },
  google_ads: { label: "Google Ads Lead Form", icon: Chrome, tint: "bg-amber-50 text-amber-600" },
};
const channelOf = (p) => CHANNELS[p?.channel_type] || CHANNELS.webhook;
const MASKED = (v) => typeof v === "string" && v.includes("•");

export default function Integrations() {
  const { viewRole } = useAuth();
  const isAdmin = ["super_admin", "dean"].includes(viewRole);
  const [publishers, setPublishers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [mapFor, setMapFor] = useState(null);
  const [newPubOpen, setNewPubOpen] = useState(false);

  const fetchAll = useCallback(() => {
    api.get("/integrations/publishers").then((r) => setPublishers(r.data)).catch(() => {});
    api.get("/integrations/logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/integrations/custom-fields").then((r) => setCustomFields(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  const toggleActive = async (p) => {
    await api.patch(`/integrations/publishers/${p.id}`, { active: !p.active });
    fetchAll();
  };

  const sendTest = async (p) => {
    const stamp = Date.now();
    try {
      if (p.channel_type === "meta_ads") {
        // simulated Meta leadgen entry with inline field_data (no leadgen_id →
        // no Graph API call needed) so a test lead is captured instantly
        const r = await api.post(`/v1/webhooks/meta/${p.slug}`, {
          object: "page",
          entry: [{
            id: "0", time: Math.floor(stamp / 1000),
            changes: [{
              field: "leadgen",
              value: {
                form_id: "test-form", page_id: "0", created_time: Math.floor(stamp / 1000),
                field_data: [
                  { name: "full_name", values: ["Meta Test Lead"] },
                  { name: "email", values: [`meta.test.${stamp}@example.com`] },
                  { name: "phone_number", values: [`96${String(stamp).slice(-8)}`] },
                  { name: "city", values: ["Mumbai"] },
                ],
              },
            }],
          }],
        });
        toast.success(`Ingested ${r.data.leads || 0} lead(s) (${r.data.status})`);
      } else if (p.channel_type === "google_ads") {
        const r = await api.post(`/v1/webhooks/google/${p.slug}`, {
          google_key: p.google_key,
          lead_id: `test-${stamp}`,
          campaign_id: "test-campaign",
          api_version: "1.0",
          is_test: false,
          user_column_data: [
            { column_id: "FULL_NAME", column_name: "Full Name", string_value: "Google Test Lead" },
            { column_id: "EMAIL", column_name: "Email", string_value: `google.test.${stamp}@example.com` },
            { column_id: "PHONE_NUMBER", column_name: "Phone Number", string_value: `+9197${String(stamp).slice(-8)}` },
            { column_id: "CITY", column_name: "City", string_value: "Bengaluru" },
          ],
        });
        toast.success(`Ingested as ${r.data.status}`);
      } else {
        const reverse = Object.fromEntries(Object.entries(p.field_mapping || {}).map(([k, v]) => [k, v]));
        const key = (sys) => reverse[sys] || sys;
        const sample = {
          [key("name")]: "Test Webhook Lead",
          [key("email")]: `webhook.test.${stamp}@example.com`,
          [key("phone")]: `98${String(stamp).slice(-8)}`,
          [key("state")]: "Maharashtra",
          [key("course")]: "MBA",
        };
        const r = await api.post(`/v1/webhooks/ingest/${p.slug}`, sample, { headers: { "X-Webhook-Token": p.token } });
        toast.success(`Ingested as ${r.data.tier} (duplicate=${r.data.duplicate})`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    fetchAll();
  };

  return (
    <div data-testid="integrations-page" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Integrations & Webhooks</h1>
          <p className="text-sm text-slate-500 mt-1">Generic webhooks plus native Meta Lead Ads &amp; Google Ads Lead Form connectors, field mapping and custom fields.</p>
        </div>
        <Dialog open={newPubOpen} onOpenChange={setNewPubOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-publisher-button" className="bg-brand w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-1.5" /> Add Connector
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Lead Connector</DialogTitle></DialogHeader>
            <NewPublisherForm onDone={() => { setNewPubOpen(false); fetchAll(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="publishers">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="publishers" data-testid="integrations-tab-publishers">Publishers & Webhooks</TabsTrigger>
          <TabsTrigger value="custom-fields" data-testid="integrations-tab-custom-fields">Custom Field Definitions</TabsTrigger>
          <TabsTrigger value="logs" data-testid="integrations-tab-logs">Ingestion Logs</TabsTrigger>
          {isAdmin && <TabsTrigger value="notifications" data-testid="integrations-tab-notifications">Notifications</TabsTrigger>}
          {isAdmin && <TabsTrigger value="gateways" data-testid="integrations-tab-gateways">Payments &amp; Gateways</TabsTrigger>}
        </TabsList>

        <TabsContent value="publishers" className="mt-4 space-y-4">
          {publishers.map((p, i) => {
            const unlimited = p.daily_lead_limit === -1;
            const pct = (p.daily_lead_limit && !unlimited) ? Math.min(100, Math.round((p.leads_today / p.daily_lead_limit) * 100)) : 0;
            const ch = channelOf(p);
            const ChIcon = ch.icon;
            return (
              <Card key={p.id} className="p-5 border-slate-200" data-testid={`publisher-card-${i}`}>
                <div className="flex flex-wrap items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ch.tint}`}>
                    <ChIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800">{p.name}</span>
                      <Badge variant="secondary" className="text-[10px]">{ch.label}</Badge>
                      <Badge className={p.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}>
                        {p.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <ConnectorSetup publisher={p} i={i} copy={copy} onSaved={fetchAll} />
                  </div>
                  <div className="w-48">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Daily quota</span>
                      <span className="font-mono" data-testid={`quota-${i}`}>{p.leads_today}/{unlimited ? "∞" : p.daily_lead_limit}</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                    <div className="text-xs text-slate-400 mt-1">Lifetime: {p.total_leads} leads</div>
                    <div className="text-[11px] text-slate-400 mt-1" title="Daily ingestion limits are set by EduPulse to match your plan and cannot be changed from this page.">
                      To increase your daily campaign intake, contact your account manager or platform support.
                    </div>
                  </div>
                  <div className="flex flex-col items-start sm:items-end gap-2 w-full sm:w-auto">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Active</span>
                      <Switch data-testid={`publisher-active-toggle-${i}`} checked={p.active} onCheckedChange={() => toggleActive(p)} />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" data-testid={`field-mapping-button-${i}`} onClick={() => setMapFor(p)}>
                        Field Mapping
                      </Button>
                      <Button variant="outline" size="sm" data-testid={`send-test-webhook-${i}`} onClick={() => sendTest(p)}>
                        <Send className="w-3.5 h-3.5 mr-1" /> Test
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="custom-fields" className="mt-4">
          <Card className="border-slate-200">
            <div className="p-4 flex items-center justify-between border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Custom Lead Fields</h2>
              <NewCustomField onDone={fetchAll} />
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Label", "Type", "Options", "Required"].map((h) => (
                    <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {customFields.map((f, i) => (
                  <TableRow key={f.id} data-testid={`custom-field-row-${i}`}>
                    <TableCell className="font-medium text-slate-800">{f.label}</TableCell>
                    <TableCell><Badge variant="secondary" className="font-mono text-xs">{f.field_type}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-500">{(f.options || []).join(", ") || "—"}</TableCell>
                    <TableCell className="text-sm">{f.required ? "Yes" : "No"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <Card className="border-slate-200" data-testid="ingest-logs-card">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Time", "Publisher Slug", "Status", "Detail"].map((h) => (
                    <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-400">No ingestion events yet. Send a test webhook.</TableCell></TableRow>
                )}
                {logs.map((l, i) => (
                  <TableRow key={l.id} data-testid={`ingest-log-row-${i}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{l.created_at?.replace("T", " ").slice(0, 19)}</TableCell>
                    <TableCell className="font-mono text-xs">{l.publisher_slug}</TableCell>
                    <TableCell>
                      <Badge className={l.status === "accepted" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>{l.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 font-mono">{l.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="notifications" className="mt-4">
            <NotificationsPanel />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="gateways" className="mt-4">
            <GatewaySettings />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!mapFor} onOpenChange={(o) => !o && setMapFor(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="field-mapping-dialog">
          <DialogHeader><DialogTitle>Payload Field Mapping — {mapFor?.name}</DialogTitle></DialogHeader>
          {mapFor && <FieldMappingEditor publisher={mapFor} onDone={() => { setMapFor(null); fetchAll(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CopyRow({ label, value, onCopy, testid }) {
  return (
    <div className="mt-2">
      {label && <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>}
      <div className="flex items-center gap-2 bg-slate-50 rounded-md px-3 py-1.5">
        <code className="font-mono text-xs text-slate-600 truncate flex-1" data-testid={testid}>{value || "—"}</code>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" aria-label={`Copy ${label || "value"}`}
          data-testid={`copy-${testid}`} onClick={() => onCopy(value, label || "Value")}>
          <Copy className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

function ConnectorSetup({ publisher: p, i, copy, onSaved }) {
  const [pat, setPat] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [busy, setBusy] = useState(false);

  const saveSecrets = async () => {
    const payload = {};
    if (pat.trim()) payload.page_access_token = pat.trim();
    if (appSecret.trim()) payload.app_secret = appSecret.trim();
    if (!Object.keys(payload).length) { toast.info("Enter a value to save"); return; }
    setBusy(true);
    try {
      await api.patch(`/integrations/publishers/${p.id}`, payload);
      setPat(""); setAppSecret("");
      toast.success("Meta credentials saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (p.channel_type === "meta_ads") {
    return (
      <div className="mt-1" data-testid={`connector-setup-meta-${i}`}>
        <p className="text-xs text-slate-500 mt-1">
          Meta App → <b>Webhooks → Page “leadgen”</b>: paste the callback URL + verify token, then store a Page access token here.
        </p>
        <CopyRow label="Callback URL" value={`${API}/v1/webhooks/meta/${p.slug}`} onCopy={copy} testid={`meta-callback-${i}`} />
        <CopyRow label="Verify Token" value={p.verify_token} onCopy={copy} testid={`meta-verify-${i}`} />
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          <Input data-testid={`meta-pat-${i}`} className="h-9 font-mono text-xs" value={pat}
            placeholder={p.page_access_token ? `stored: ${p.page_access_token}` : "Page access token"}
            onChange={(e) => setPat(e.target.value)} />
          <Input data-testid={`meta-appsecret-${i}`} className="h-9 font-mono text-xs" value={appSecret}
            placeholder={p.app_secret ? `stored: ${p.app_secret}` : "App secret (optional, verifies signature)"}
            onChange={(e) => setAppSecret(e.target.value)} />
        </div>
        <Button size="sm" className="bg-brand mt-2" data-testid={`meta-save-${i}`} disabled={busy} onClick={saveSecrets}>
          {busy ? "Saving…" : "Save credentials"}
        </Button>
      </div>
    );
  }

  if (p.channel_type === "google_ads") {
    return (
      <div className="mt-1" data-testid={`connector-setup-google-${i}`}>
        <p className="text-xs text-slate-500 mt-1">
          Google Ads → <b>Lead form → Webhook integration</b>: paste the webhook URL and the key below.
        </p>
        <CopyRow label="Webhook URL" value={`${API}/v1/webhooks/google/${p.slug}`} onCopy={copy} testid={`google-url-${i}`} />
        <CopyRow label="Key" value={p.google_key} onCopy={copy} testid={`google-key-${i}`} />
      </div>
    );
  }

  return (
    <div className="mt-1" data-testid={`connector-setup-webhook-${i}`}>
      <CopyRow label="Webhook URL" value={`${API}/v1/webhooks/ingest/${p.slug}`} onCopy={copy} testid={`webhook-url-${i}`} />
      <CopyRow label="X-Webhook-Token" value={p.token} onCopy={copy} testid={`webhook-token-${i}`} />
    </div>
  );
}

function FieldMappingEditor({ publisher, onDone }) {
  const [mapping, setMapping] = useState({ ...publisher.field_mapping });
  const [pat, setPat] = useState("");
  const isMeta = publisher.channel_type === "meta_ads";
  const save = async () => {
    const cleaned = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v && v.trim()));
    const payload = { field_mapping: cleaned };
    if (isMeta && pat.trim()) payload.page_access_token = pat.trim();
    await api.patch(`/integrations/publishers/${publisher.id}`, payload);
    toast.success("Field mapping saved");
    onDone();
  };
  const exampleKey = isMeta ? "full_name" : publisher.channel_type === "google_ads" ? "FULL_NAME" : "full_name";
  return (
    <div className="space-y-3">
      {isMeta && (
        <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
          <Label className="text-xs">Page Access Token (optional)</Label>
          <Input data-testid="mapping-page-access-token" className="mt-1 h-9 font-mono text-xs" value={pat}
            placeholder={publisher.page_access_token ? `stored: ${publisher.page_access_token}` : "EAAG… — lets EduPulse fetch each lead from the Meta Graph API"}
            onChange={(e) => setPat(e.target.value)} />
          <p className="text-[11px] text-slate-400 mt-1">Without it, only simulated / already-flat payloads are ingested.</p>
        </div>
      )}
      <p className="text-xs text-slate-500">
        Map each CRM field to the key in the {isMeta ? "Meta field_data" : publisher.channel_type === "google_ads" ? "Google user_column_data (column_id)" : "publisher JSON"} payload. Unmapped keys become custom attributes.
      </p>
      {SYSTEM_FIELDS.map((f) => (
        <div key={f} className="grid grid-cols-[7rem_1fr] gap-3 items-center">
          <div className="text-sm font-medium text-slate-700 font-mono truncate">{f}</div>
          <Input data-testid={`mapping-input-${f}`} value={mapping[f] || ""} placeholder={`payload key (e.g. ${exampleKey})`}
            onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value }))} className="h-9 font-mono text-xs" />
        </div>
      ))}
      <Button data-testid="save-mapping-button" onClick={save} className="w-full bg-brand mt-2">Save Mapping</Button>
    </div>
  );
}

function NewPublisherForm({ onDone }) {
  const [name, setName] = useState("");
  const [channelType, setChannelType] = useState("webhook");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/integrations/publishers", {
        name, channel_type: channelType,
      });
      toast.success(`${CHANNELS[channelType].label} connector created`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="new-publisher-form">
      <div>
        <Label>Connector Type</Label>
        <Select value={channelType} onValueChange={setChannelType}>
          <SelectTrigger data-testid="new-publisher-channel" className="mt-1.5"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(CHANNELS).map(([v, c]) => <SelectItem key={v} value={v}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{channelType === "webhook" ? "Publisher Name" : "Connector Name"}</Label>
        <Input data-testid="new-publisher-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5"
          placeholder={channelType === "meta_ads" ? "e.g. Meta Lead Ads" : channelType === "google_ads" ? "e.g. Google Lead Form" : "e.g. Careers360"} />
      </div>
      <p className="text-xs text-slate-400">
        The new connector's daily lead limit is set to your plan's quota automatically —
        contact platform support to raise it.
      </p>
      <Button data-testid="new-publisher-submit" type="submit" disabled={busy} className="w-full bg-brand">
        {busy ? "Creating…" : "Create Connector"}
      </Button>
    </form>
  );
}

function NewCustomField({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", field_type: "text", options: "", required: false });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/integrations/custom-fields", form);
      toast.success("Custom field added");
      setOpen(false);
      setForm({ label: "", field_type: "text", options: "", required: false });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="new-custom-field-button"><Plus className="w-4 h-4 mr-1" /> Add Field</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Custom Field</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="custom-field-form">
          <div>
            <Label>Label</Label>
            <Input data-testid="custom-field-label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} required className="mt-1.5" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.field_type} onValueChange={(v) => setForm((f) => ({ ...f, field_type: v }))}>
              <SelectTrigger data-testid="custom-field-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(form.field_type === "select" || form.field_type === "multi_select") && (
            <div>
              <Label>Options (comma separated)</Label>
              <Input data-testid="custom-field-options" value={form.options} onChange={(e) => setForm((f) => ({ ...f, options: e.target.value }))} className="mt-1.5" />
            </div>
          )}
          <Button data-testid="custom-field-submit" type="submit" className="w-full bg-brand">Create Field</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
